import React, { useState, useEffect } from 'react';
import { FileText, AlertCircle, Mic, Volume2, Clock, MessageSquare, ChevronDown, ChevronRight } from 'lucide-react';
import ErrorBoundary from '../shared/ErrorBoundary';
import MessageList from '../shared/MessageList';
import { ToolResultCard, parseDoubleEncoded } from '../shared/ToolCard';
import SectionHeader from '../shared/SectionHeader';
import JSONTreeViewer from '../../../../components/JSONTreeViewer';
import { extractInputOutputMessages } from '../../../../utils/openinference';
import { extractFinalOutput } from '../../../../utils/traceIoParser';
import { isLiveKitSpan, extractLiveKitOutput, extractRetrieverDocuments } from '../../../../utils/traceSpanIO';
import { typography, palette, withOpacity } from '../../../../utils/designTokens';
import { isJsonMime } from '../../../../utils/mimeType';
import { logger } from '../../../../utils/logger';


/**
 * Format LiveKit metrics for display
 */
const formatMetricValue = (key, value) => {
  if (value === null || value === undefined) return '-';

  // Time values (in seconds)
  if (key.includes('duration') || key.includes('ttfb') || key.includes('ttft') || key.includes('delay')) {
    const ms = value * 1000;
    if (ms < 1000) return `${Math.round(ms)}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  }

  // Probability/percentage values
  if (key.includes('probability') || key.includes('threshold')) {
    return `${(value * 100).toFixed(1)}%`;
  }

  // Token counts
  if (key.includes('tokens')) {
    return value.toLocaleString();
  }

  // Default formatting
  if (typeof value === 'number') {
    return Number.isInteger(value) ? value.toLocaleString() : value.toFixed(3);
  }

  return String(value);
};

const formatDocumentScore = (score) => {
  if (score === null || score === undefined) return null;
  const numericScore = typeof score === 'number'
    ? score
    : typeof score === 'string' && score.trim() !== ''
      ? Number(score)
      : NaN;
  return Number.isFinite(numericScore) ? numericScore.toFixed(4) : String(score);
};

const _contentHasText = (content) => {
  if (content === null || content === undefined) return false;
  if (typeof content === 'string') return content !== '';
  if (typeof content === 'number' || typeof content === 'boolean') return true;
  if (Array.isArray(content)) return content.some(_contentHasText);
  if (typeof content === 'object') {
    if (typeof content.text === 'string') return content.text !== '';
    if (Array.isArray(content.parts)) return content.parts.some(_contentHasText);
    if (content.function_call || content.function_response || content.tool_use || content.tool_result) return true;
    return true;
  }
  return false;
};

const _normalizeMsgs = (raw) => {
  if (!Array.isArray(raw)) return [];
  return raw.map(m => {
    const msg = m?.message?.role ? m.message : m?.role ? m : null;
    if (!msg) return null;
    return {
      ...msg,
      role: msg.role === 'model' ? 'assistant' : msg.role,
      content: msg.content ?? msg.parts ?? msg.text ?? '',
    };
  }).filter(m => {
    if (!m) return false;
    return _contentHasText(m.content) || (Array.isArray(m.tool_calls) && m.tool_calls.length > 0);
  });
};

/**
 * Resolve assistant_response to displayable text. Spans ingested before the
 * worker-side extraction fix (extract_llm_output in span_parser.py) have the
 * whole raw provider envelope (e.g. LiteLLM/Gemini's
 * {"content":{"parts":[{"text":"..."}]},...}) sitting in assistant_response
 * verbatim — showing that as plain text in a chat bubble is a downgrade from
 * even the old JSONTreeViewer fallback. If it looks like JSON, try the same
 * shape-aware extraction already used by the trace list/outline views
 * (traceIoParser's extractFinalOutput) before falling back to the raw text.
 */
const _resolveAssistantResponseText = (assistantResponse) => {
  if (typeof assistantResponse !== 'string') return assistantResponse;
  const trimmed = assistantResponse.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return assistantResponse;
  const extracted = extractFinalOutput({ text: assistantResponse, span_kind: 'LLM' });
  return extracted || assistantResponse;
};

const OutputTab = ({ span, spanKind, showRaw = false, drilldownApi = null, drilldownMessages = null, drilldownMessagesLoading = false, drilldownMessagesError = null, hideErrorHeader = false, forceErrorOutput = false, suppressErrorOutput = false }) => {
  if (!span) {
    throw new Error('OutputTab requires span prop');
  }

  // Parse attributes from JSON string (Data API returns attributes_json as string)
  let attributes = {};
  if (typeof span.attributes === 'string') {
    try {
      attributes = JSON.parse(span.attributes);
    } catch (e) {
      logger.error('Failed to parse span attributes:', e);
    }
  } else if (span.attributes) {
    attributes = span.attributes;
  }

  const isV2Span = span.__v2Span === true;
  const spanErrorDetection = Array.isArray(span.detections)
    ? span.detections.find(detection => detection?.classifier_key === 'error_detection' && (detection?.status === 'detected' || detection?.detected))
    : null;
  const hasErrorOutput = span.status_code === 'ERROR' || span.metrics?.has_error || Boolean(spanErrorDetection);

  const [drilldownDocuments, setDrilldownDocuments] = useState(null);
  const [drilldownDocumentsError, setDrilldownDocumentsError] = useState(null);
  useEffect(() => {
    setDrilldownDocuments(null);
    setDrilldownDocumentsError(null);
    if (!drilldownApi || !['retriever', 'reranker'].includes(spanKind)) return;
    let cancelled = false;
    drilldownApi.fetchDocuments()
      .then(data => { if (!cancelled) setDrilldownDocuments(data); })
      .catch(err => { if (!cancelled) setDrilldownDocumentsError(err?.message || 'Failed to load documents'); });
    return () => { cancelled = true; };
  }, [span.span_id, drilldownApi]); // eslint-disable-line

  let retrieverDocuments = [];
  if (!isV2Span && spanKind === 'retriever') {
    retrieverDocuments = extractRetrieverDocuments(span);
  }

  // Check if this is a LiveKit span
  const isLiveKit = isLiveKitSpan(span);
  const liveKitSections = (!isV2Span && isLiveKit) ? extractLiveKitOutput(span) : [];

  // Icon mapping for LiveKit sections
  const getIcon = (iconName) => {
    switch (iconName) {
      case 'mic': return <Mic size={16} style={{ color: palette.accents.primary.base }} />;
      case 'volume': return <Volume2 size={16} style={{ color: palette.accents.primary.base }} />;
      case 'clock': return <Clock size={16} style={{ color: palette.accents.primary.base }} />;
      case 'message': return <MessageSquare size={16} style={{ color: palette.accents.primary.base }} />;
      default: return null;
    }
  };

  // Compute formatted and raw output content
  let formattedOutput = null;
  let rawOutput = null;

  if (spanKind === 'llm') {
    if (isV2Span) {
      const outputMsgs = _normalizeMsgs(drilldownMessages?.output_messages);
      if (outputMsgs.length > 0) {
        formattedOutput = <MessageList messages={outputMsgs} type="output" />;
      } else if ((drilldownMessages !== null || !drilldownApi) && span.assistant_response) {
        // Prefer the pre-extracted assistant_response as the formatted view
        // whenever it's available. output_value being present doesn't mean
        // it's "better formatted" — for spans where the instrumentation
        // forwards a raw provider response envelope (e.g. LiteLLM/Gemini),
        // output_value is the whole unparsed JSON blob, and showing that via
        // JSONTreeViewer as "output" is actually a downgrade from the
        // extracted text. The raw envelope is still available in full via
        // the showRaw toggle below (rawOutput, from output_value).
        //
        // assistant_response itself may still be a raw JSON envelope for
        // spans ingested before the worker-side extraction fix — resolve it
        // client-side so already-ingested spans display cleanly too, not
        // just newly-ingested ones.
        formattedOutput = <MessageList messages={[{ role: 'assistant', content: _resolveAssistantResponseText(span.assistant_response) }]} type="output" />;
      }
    } else {
      try {
        const { output: outputMessages } = extractInputOutputMessages(span.attributes);
        if (outputMessages && outputMessages.length > 0) {
          formattedOutput = <MessageList messages={outputMessages} type="output" />;
        }
      } catch (e) {
        logger.error('Failed to extract LLM output messages:', e);
      }

      // v1: try output_messages attrs before falling back to output_value
      const llmAttrs = attributes?.llm || {};
      const rawOutputMessages = llmAttrs.output_messages;
      if (rawOutputMessages && (Array.isArray(rawOutputMessages) ? rawOutputMessages.length > 0 : Object.keys(rawOutputMessages).length > 0)) {
        const jsonString = typeof rawOutputMessages === 'string' ? rawOutputMessages : JSON.stringify(rawOutputMessages);
        rawOutput = <JSONTreeViewer data={jsonString} />;
      }
    }

    if (!rawOutput && span.output_value) {
      // Text unless the span declares JSON. This replaces a content-shape guess (does it start
      // with { or [) that existed only because output_mime_type was never returned by the API.
      if (!isJsonMime(span.output_mime_type)) {
        rawOutput = <MessageList messages={[{ role: 'assistant', content: span.output_value }]} type="output" />;
      } else {
        rawOutput = <JSONTreeViewer data={span.output_value} mimeType={span.output_mime_type} truncated={!!span.output_value_truncated} />;
      }
    }
  } else {
    // Non-LLM spans: output_value is the main content
    if (span.output_value && !(spanKind === 'retriever' && retrieverDocuments.length > 0)) {
      if (spanKind === 'tool') {
        // Tool spans: structured card view, with raw JSON available via toggle
        formattedOutput = <ToolResultCard name={span.name || span.span_name || 'Tool'} data={parseDoubleEncoded(span.output_value)} />;
        rawOutput = <JSONTreeViewer data={span.output_value} mimeType={span.output_mime_type} truncated={!!span.output_value_truncated} />;
      } else {
        // Text unless the span declares JSON — the same rule as the LLM branch above.
        if (!isJsonMime(span.output_mime_type)) {
          formattedOutput = (
            <div
              className="p-3 rounded border font-mono"
              style={{
                backgroundColor: palette.neutrals.canvas,
                borderColor: palette.borders.subtle,
                color: palette.text.primary,
                fontSize: typography.size.sm,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}
            >
              {span.output_value}
            </div>
          );
        } else {
          formattedOutput = <JSONTreeViewer data={span.output_value} mimeType={span.output_mime_type} truncated={!!span.output_value_truncated} />;
        }
      }
    }
  }

  // When showRaw, render raw content only
  if (showRaw) {
    return (
      <ErrorBoundary>
        {rawOutput || <div className="p-4 text-sm text-text-muted">No raw output data available.</div>}
      </ErrorBoundary>
    );
  }

  // While the /messages drilldown is in flight, falling back to a TRUNCATED output_value preview
  // shows ~2s of JSON cut mid-string — unreadable, and replaced the moment the drilldown lands.
  // Hold a loading line instead of flashing it.
  //
  // Deliberately narrow: only v2 LLM spans fetch a drilldown at all (chain/agent/tool spans never
  // do, so a placeholder there would hang forever), and only a truncated preview is worth hiding —
  // a complete value is readable now and should not be delayed. On failure drilldownMessagesLoading
  // clears and the preview returns, preserving the existing fallback-to-partial behaviour.
  const awaitingFullOutput = isV2Span
    && spanKind === 'llm'
    && drilldownMessagesLoading
    && !formattedOutput
    && !!span.output_value_truncated;

  const mainContent = formattedOutput
    || (awaitingFullOutput
      ? <div className="p-3 text-sm" style={{ color: palette.text.muted }}>Loading…</div>
      : rawOutput);

  const outputTruncatedNotice = isV2Span && spanKind === 'llm' && drilldownMessages?.output_messages_truncated
    ? 'Full output messages exceed the display limit. Showing assistant response preview only.'
    : null;

  return (
    <ErrorBoundary>
      <div className="space-y-3">
        {outputTruncatedNotice && !forceErrorOutput && (
          <div className="px-3 py-2 rounded text-xs italic" style={{ backgroundColor: palette.neutrals.panelSecondary, color: palette.text.muted }}>
            {outputTruncatedNotice}
          </div>
        )}
        {!forceErrorOutput && mainContent}

        {/* v2 LLM: tool_calls from drilldown when output_messages is absent */}
        {!forceErrorOutput && isV2Span && spanKind === 'llm' && !showRaw &&
          drilldownMessages?.tool_calls?.length > 0 &&
          !drilldownMessages?.output_messages?.length && (
          <div>
            <SectionHeader title={`Tool Calls (${drilldownMessages.tool_calls.length})`} />
            <JSONTreeViewer data={JSON.stringify(drilldownMessages.tool_calls)} />
          </div>
        )}
        {isV2Span && spanKind === 'llm' && !showRaw && drilldownMessages?.tool_calls_truncated && (
          <div className="px-3 py-2 rounded text-xs italic" style={{ backgroundColor: palette.neutrals.panelSecondary, color: palette.text.muted }}>
            Tool calls list exceeds the display limit and was omitted.
          </div>
        )}

        {/* Message/document fetch error banners */}
        {isV2Span && spanKind === 'llm' && drilldownMessagesError && !mainContent && (
          <div className="p-3 text-sm rounded border" style={{ color: palette.status.danger, borderColor: withOpacity(palette.status.danger, 25), backgroundColor: withOpacity(palette.status.danger, 6) }}>
            Failed to load output messages: {drilldownMessagesError}
          </div>
        )}
        {isV2Span && ['retriever', 'reranker'].includes(spanKind) && drilldownDocumentsError && (
          <div className="p-3 text-sm rounded border" style={{ color: palette.status.danger, borderColor: withOpacity(palette.status.danger, 25), backgroundColor: withOpacity(palette.status.danger, 6) }}>
            Failed to load documents: {drilldownDocumentsError}
          </div>
        )}

        {/* LiveKit Output Sections */}
        {isLiveKit && liveKitSections.map((section, idx) => (
          <div key={idx} className="pt-3 border-t" style={{ borderColor: palette.borders.subtle }}>
            <SectionHeader
              title={section.title}
              icon={getIcon(section.icon)}
              copyText={typeof section.content === 'string' ? section.content : JSON.stringify(section.content, null, 2)}
              copyLabel={`Copy ${section.title}`}
            />
            {section.type === 'transcript' || section.type === 'response' ? (
              <div
                className="p-3 rounded border font-mono text-sm whitespace-pre-wrap"
                style={{
                  backgroundColor: palette.neutrals.canvas,
                  borderColor: palette.borders.subtle,
                  color: palette.text.primary
                }}
              >
                {section.content}
              </div>
            ) : section.type === 'metrics' ? (
              <div
                className="p-3 rounded border"
                style={{
                  backgroundColor: palette.neutrals.canvas,
                  borderColor: palette.borders.subtle
                }}
              >
                <div className="space-y-1">
                  {Object.entries(section.content)
                    .filter(([key, value]) => value !== null && value !== undefined && key !== 'type')
                    .map(([key, value]) => {
                      const formattedValue = formatMetricValue(key, value);
                      return (
                        <div key={key} className="flex justify-between items-center gap-4 py-1 border-b last:border-b-0" style={{ borderColor: palette.borders.subtle }}>
                          <span
                            className="text-xs flex-shrink-0"
                            style={{ color: palette.text.muted }}
                          >
                            {key.replace(/_/g, ' ')}
                          </span>
                          <span
                            className="text-sm font-mono font-semibold truncate max-w-[200px] cursor-default"
                            style={{ color: palette.text.primary }}
                            title={formattedValue}
                          >
                            {formattedValue}
                          </span>
                        </div>
                      );
                    })}
                </div>
              </div>
            ) : (
              <JSONTreeViewer data={typeof section.content === 'string' ? section.content : JSON.stringify(section.content)} />
            )}
          </div>
        ))}

        {/* v2 RETRIEVER — query + count from direct fields; full docs from drilldown */}
        {isV2Span && spanKind === 'retriever' && (span.retriever_query || span.retriever_document_count != null) && (
          <div className="space-y-1.5">
            <SectionHeader title="Retriever" />
            {span.retriever_query && (
              <div className="space-y-1">
                <span className="text-xs font-mono" style={{ color: palette.support.attributeLabel }}>Query</span>
                <div
                  className="p-3 rounded border font-mono text-sm whitespace-pre-wrap"
                  style={{ backgroundColor: palette.neutrals.canvas, borderColor: palette.borders.subtle, color: palette.text.primary }}
                >
                  {span.retriever_query}
                </div>
              </div>
            )}
            {span.retriever_document_count != null && (
              <div className="flex justify-between items-center">
                <span className="text-xs font-mono" style={{ color: palette.support.attributeLabel }}>Documents</span>
                <span className="text-xs font-mono" style={{ color: palette.accents.primary.base }}>{span.retriever_document_count}</span>
              </div>
            )}
            {drilldownDocuments?.retriever_documents?.length > 0 && (
              <div className="space-y-2 pt-1">
                {drilldownDocuments.retriever_documents.map((doc, idx) => {
                  const formattedScore = formatDocumentScore(doc.score);
                  return (
                    <div key={idx} className="rounded border p-3" style={{ backgroundColor: palette.neutrals.panelSecondary, borderColor: palette.borders.medium }}>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-xs font-semibold" style={{ color: palette.text.primary }}>{doc.id || `Document ${idx + 1}`}</span>
                        {formattedScore !== null && (
                          <span className="px-2 py-0.5 rounded font-mono text-xs" style={{ backgroundColor: withOpacity(palette.accents.primary.base, 13), color: palette.accents.primary.base }}>{formattedScore}</span>
                        )}
                      </div>
                      <pre className="text-xs whitespace-pre-wrap break-words" style={{ color: palette.text.primary, fontFamily: 'inherit', margin: 0 }}>{doc.content}</pre>
                    </div>
                  );
                })}
              </div>
            )}
            {drilldownDocuments?.retriever_documents_truncated && (
              <div className="text-xs italic pt-1" style={{ color: palette.text.muted }}>
                Document list too large to display in full.
              </div>
            )}
          </div>
        )}

        {/* RETRIEVER Documents */}
        {retrieverDocuments.length > 0 && (
          <div>
            <SectionHeader
              title={`Documents (${retrieverDocuments.length})`}
              copyText={JSON.stringify(retrieverDocuments, null, 2)}
              copyLabel="Copy Documents as JSON"
            />
            <div className="space-y-3">
              {retrieverDocuments.map((doc, idx) => {
                const formattedScore = formatDocumentScore(doc.score);
                return (
                  <div
                    key={idx}
                    className="rounded border p-3"
                    style={{
                      backgroundColor: palette.neutrals.panelSecondary,
                      borderColor: palette.borders.medium
                    }}
                  >
                    {/* Document Header */}
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex items-center gap-2">
                        <FileText
                          style={{
                            width: '16px',
                            height: '16px',
                            color: palette.accents.primary.base
                          }}
                        />
                        <span className={`${typography.size.sm} font-semibold`} style={{ color: palette.text.primary }}>
                          {doc.id || `Document ${idx + 1}`}
                        </span>
                      </div>
                      {formattedScore !== null && (
                        <span
                          className="px-2 py-0.5 rounded font-mono"
                          style={{
                            fontSize: typography.size.xs,
                            backgroundColor: withOpacity(palette.accents.primary.base, 13),
                            color: palette.accents.primary.base,
                            fontWeight: typography.weight.semibold
                          }}
                        >
                          score {formattedScore}
                        </span>
                      )}
                    </div>

                    {/* Document Content */}
                    <div
                      className="p-2 rounded mb-2"
                      style={{ backgroundColor: palette.neutrals.canvas }}
                    >
                      <pre
                        className={`${typography.size.sm} whitespace-pre-wrap break-words`}
                        style={{
                          color: palette.text.primary,
                          fontFamily: 'inherit',
                          margin: 0
                        }}
                      >
                        {doc.content}
                      </pre>
                    </div>

                    {/* Document Metadata - Collapsible */}
                    {doc.metadata && (
                      <details>
                        <summary
                          className="cursor-pointer hover:underline"
                          style={{
                            fontSize: typography.size.xs,
                            color: palette.text.secondary,
                            fontWeight: typography.weight.semibold
                          }}
                        >
                          Metadata
                        </summary>
                        <div className="mt-2">
                          <JSONTreeViewer
                            data={doc.metadata}
                            maxHeight="300px"
                            showControls={false}
                          />
                        </div>
                      </details>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Error Output */}
        {hasErrorOutput && !suppressErrorOutput && (forceErrorOutput || !span.output_value) && (() => {
          // v2: error_message direct field; v1: metrics.error_message or status_message
          let errorMessage = (isV2Span ? span.error_message : null) || span.metrics?.error_message || span.status_message || spanErrorDetection?.evidence || spanErrorDetection?.reason || '';

          // v2: exception_stacktraces array from span detail
          if (isV2Span && Array.isArray(span.exception_stacktraces) && span.exception_stacktraces.length > 0) {
            const stack = span.exception_stacktraces[0];
            if (stack && !errorMessage.includes('Traceback')) {
              errorMessage = (errorMessage ? errorMessage + '\n' : '') + stack;
            }
          }

          // v1: check span events for exception details
          let eventsArray = (!isV2Span && span.events) ? span.events : [];
          if (typeof eventsArray === 'string') {
            try {
              eventsArray = JSON.parse(eventsArray);
            } catch (e) {
              eventsArray = [];
            }
          }

          // Extract exception.stacktrace from events if available
          for (const event of eventsArray) {
            let eventName, eventAttrs;
            if (Array.isArray(event) && event.length >= 3) {
              // Tuple format: (timestamp, name, attributes)
              eventName = event[1];
              eventAttrs = event[2];
            } else if (event && typeof event === 'object') {
              eventName = event.name;
              eventAttrs = event.attributes;
            }

            if (eventName === 'exception' && eventAttrs) {
              const stacktrace = eventAttrs['exception.stacktrace'];
              const exceptionMessage = eventAttrs['exception.message'];
              if (stacktrace && !errorMessage.includes('Traceback')) {
                // Prepend exception message and append stacktrace to error message
                errorMessage = (exceptionMessage ? exceptionMessage + '\n' : '') + 'Traceback (most recent call last):\n' + stacktrace.replace(/^Traceback[^\n]*\n?/i, '');
              }
              break;
            }
          }

          if (!errorMessage) errorMessage = 'Unknown error';

          // Parse error message into sections
          const parseErrorMessage = (msg) => {
            if (!msg) return { errorType: 'Unknown error', errorDetail: '', frames: [], finalError: '' };

            // Handle escaped newlines (\\n as literal text) - convert to actual newlines
            const normalizedMsg = msg.replace(/\\n/g, '\n');
            const lines = normalizedMsg.split('\n');
            let errorType = '';
            let errorDetail = '';
            let frames = [];
            let finalError = '';
            let currentFrame = null;
            let inTraceback = false;

            for (let i = 0; i < lines.length; i++) {
              const line = lines[i];

              // First line is usually the error type
              if (i === 0 && !line.startsWith('Traceback')) {
                errorType = line;
                continue;
              }

              // Start of traceback
              if (line.includes('Traceback (most recent call last)')) {
                inTraceback = true;
                continue;
              }

              // Stack frame: "  File "..."
              if (inTraceback && line.trim().startsWith('File "')) {
                if (currentFrame) frames.push(currentFrame);
                // Parse: File "/path/to/file.py", line 123, in function_name
                const match = line.match(/File "([^"]+)", line (\d+), in (.+)/);
                if (match) {
                  currentFrame = {
                    file: match[1],
                    line: match[2],
                    func: match[3],
                    code: []
                  };
                } else {
                  currentFrame = { file: line.trim(), line: '', func: '', code: [] };
                }
                continue;
              }

              // Code line in stack frame
              if (inTraceback && currentFrame && line.startsWith('    ') && !line.trim().startsWith('File "')) {
                currentFrame.code.push(line.trim());
                continue;
              }

              // Final error line (after traceback)
              if (inTraceback && !line.startsWith(' ') && line.includes(':') && !line.startsWith('File')) {
                if (currentFrame) {
                  frames.push(currentFrame);
                  currentFrame = null;
                }
                finalError = line;
                continue;
              }

              // Additional context after error
              if (finalError && line.trim()) {
                errorDetail += (errorDetail ? '\n' : '') + line;
              }
            }

            if (currentFrame) frames.push(currentFrame);

            // If we didn't parse any frames but there's a traceback, extract it as raw
            let rawTraceback = '';
            if (frames.length === 0 && normalizedMsg.includes('Traceback')) {
              const tbStart = normalizedMsg.indexOf('Traceback');
              if (tbStart > 0) {
                rawTraceback = normalizedMsg.substring(tbStart);
              }
            }

            return { errorType, errorDetail, frames, finalError: finalError || errorType, rawTraceback };
          };

          const { errorType, errorDetail, frames, finalError, rawTraceback } = parseErrorMessage(errorMessage);

          // Shorten file paths for display
          const shortenPath = (path) => {
            const parts = path.split('/');
            if (parts.length <= 3) return path;
            return '.../' + parts.slice(-3).join('/');
          };

          return (
            <div className="space-y-3 pb-3">
              {!hideErrorHeader && (
                <SectionHeader
                  title="Error"
                  copyText={errorMessage}
                  copyLabel="Copy Full Error"
                />
              )}

              {/* Error Summary */}
              <div
                className="p-3 rounded border"
                style={{
                  backgroundColor: withOpacity(palette.status.danger, 10),
                  borderColor: withOpacity(palette.status.danger, 30)
                }}
              >
                <div className="flex items-start gap-2">
                  <AlertCircle
                    style={{
                      width: '16px',
                      height: '16px',
                      color: palette.status.danger,
                      flexShrink: 0,
                      marginTop: '2px'
                    }}
                  />
                  <div className="flex-1 min-w-0">
                    <div
                      className={`${typography.size.sm} font-semibold break-words`}
                      style={{ color: palette.status.danger }}
                    >
                      {finalError || errorType}
                    </div>
                    {errorDetail && (
                      <div
                        className={`${typography.size.xs} mt-1 break-words`}
                        style={{ color: palette.text.secondary }}
                      >
                        {errorDetail}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Stack Trace - Parsed */}
              {frames.length > 0 && (
                <details className="group">
                  <summary
                    className={`cursor-pointer select-none flex items-center gap-1 ${typography.size.metadata} font-semibold uppercase`}
                    style={{ color: palette.text.supporting }}
                  >
                    <span className="group-open:hidden"><ChevronRight size={14} /></span>
                    <span className="hidden group-open:inline"><ChevronDown size={14} /></span>
                    Stack Trace ({frames.length} frames)
                  </summary>
                  <div className="mt-3 space-y-1 pl-4">
                    {frames.map((frame, idx) => (
                      <div
                        key={idx}
                        className="p-2 rounded text-xs"
                        style={{
                          backgroundColor: palette.neutrals.canvas,
                          borderLeft: `2px solid ${idx === frames.length - 1 ? palette.status.danger : palette.borders.subtle}`
                        }}
                      >
                        <div className="flex items-center gap-2 flex-wrap">
                          <span
                            className="font-semibold"
                            style={{ color: palette.accents.primary.base }}
                          >
                            {frame.func}
                          </span>
                          <span style={{ color: palette.text.muted }}>
                            {shortenPath(frame.file)}:{frame.line}
                          </span>
                        </div>
                        {frame.code.length > 0 && (
                          <pre
                            className={`mt-1 overflow-x-auto ${typography.mono} ${typography.size.xs}`}
                            style={{ color: palette.text.secondary, margin: 0 }}
                          >
                            {frame.code.join('\n')}
                          </pre>
                        )}
                      </div>
                    ))}
                  </div>
                </details>
              )}

              {/* Stack Trace - Raw fallback when parsing fails */}
              {frames.length === 0 && rawTraceback && (
                <details className="group">
                  <summary
                    className={`cursor-pointer select-none flex items-center gap-1 ${typography.size.metadata} font-semibold uppercase`}
                    style={{ color: palette.text.supporting }}
                  >
                    <span className="group-open:hidden"><ChevronRight size={14} /></span>
                    <span className="hidden group-open:inline"><ChevronDown size={14} /></span>
                    Stack Trace
                  </summary>
                  <pre
                    className={`mt-3 p-3 rounded overflow-x-auto ${typography.mono} ${typography.size.xs}`}
                    style={{
                      backgroundColor: palette.neutrals.canvas,
                      color: palette.text.secondary,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      margin: 0
                    }}
                  >
                    {rawTraceback}
                  </pre>
                </details>
              )}
            </div>
          );
        })()}
      </div>
    </ErrorBoundary>
  );
};

export default OutputTab;
